<template>
  <div>
    <v-card-text class="py-6 px-4 d-flex flex-column" style="gap: 16px">
      <div
        class="d-flex px-4 justify-center"
        style="min-width: 300px"
        data-testid="aggregate-learning-leaderboard-preview-wrapper"
      >
        <AggregateLearningLeaderboardMinimizedView
          :config="livePreviewTitleConfig"
          :total-participants="MOCK_LEADERBOARD.totalParticipants"
          :rows="MOCK_LEADERBOARD.rows"
          :previewMode="true"
        />
      </div>

      <div class="d-flex flex-column" style="gap: 8px">
        <p class="subtitle-1 font-weight-bold">Title</p>
        <v-text-field
          v-model="title"
          label="Title"
          outlined
          dense
          hide-details
          data-testid="title-input"
        />
      </div>

      <div class="d-flex align-center" style="gap: 12px">
        <input
          type="color"
          v-model="titleColor"
          aria-label="Title color"
          data-testid="title-color-picker"
          class="color-swatch"
        />
        <v-btn
          icon
          small
          :color="titleBold ? 'brandCyan' : ''"
          :class="{ 'format-btn--active': titleBold }"
          @click="titleBold = !titleBold"
          aria-label="Bold"
          data-testid="bold-btn"
        >
          <v-icon>mdi-format-bold</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="titleItalic ? 'brandCyan' : ''"
          :class="{ 'format-btn--active': titleItalic }"
          @click="titleItalic = !titleItalic"
          aria-label="Italic"
          data-testid="italic-btn"
        >
          <v-icon>mdi-format-italic</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="titleUnderline ? 'brandCyan' : ''"
          :class="{ 'format-btn--active': titleUnderline }"
          @click="titleUnderline = !titleUnderline"
          aria-label="Underline"
          data-testid="underline-btn"
        >
          <v-icon>mdi-format-underline</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="horizontalAlignment === 'left' ? 'brandCyan' : ''"
          :class="{ 'format-btn--active': horizontalAlignment === 'left' }"
          @click="horizontalAlignment = 'left'"
          aria-label="Align left"
          data-testid="align-left-btn"
        >
          <v-icon>mdi-format-align-left</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="horizontalAlignment === 'center' ? 'brandCyan' : ''"
          :class="{ 'format-btn--active': horizontalAlignment === 'center' }"
          @click="horizontalAlignment = 'center'"
          aria-label="Align center"
          data-testid="align-center-btn"
        >
          <v-icon>mdi-format-align-center</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="horizontalAlignment === 'right' ? 'brandCyan' : ''"
          :class="{ 'format-btn--active': horizontalAlignment === 'right' }"
          @click="horizontalAlignment = 'right'"
          aria-label="Align right"
          data-testid="align-right-btn"
        >
          <v-icon>mdi-format-align-right</v-icon>
        </v-btn>
        <v-select
          v-model="titleSize"
          :items="titleSizeItems"
          label="Size"
          outlined
          dense
          hide-details
          data-testid="title-size-select"
          style="max-width: 120px"
        />
      </div>
    </v-card-text>

    <div v-if="saveError" class="px-4 pb-2">
      <v-alert type="error" dense text data-testid="save-error">
        {{ saveError }}
      </v-alert>
    </div>

    <div class="d-flex align-center justify-end px-4 pb-4" style="gap: 16px">
      <Button :dataTestId="'cancel-btn'" text color="brandPrimary" @click="handleCancel">
        Cancel
      </Button>
      <Button
        :dataTestId="'save-btn'"
        color="brandPrimary"
        :loading="saving"
        :disabled="saving"
        @click="onSave"
      >
        Save
      </Button>
    </div>

    <Modal v-model="showUnsavedModal">
      <div>Are you sure you want to exit? You will lose any unsaved changes.</div>
      <div class="d-flex justify-end pt-4" style="gap: 8px">
        <Button :dataTestId="'stay-btn'" text color="brandCyan" @click="handleStay">
          Go Back
        </Button>
        <Button :dataTestId="'discard-btn'" color="brandCyan" @click="handleDiscard">
          Discard
        </Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import Button from "@/components/generic/Button.vue";
import Modal from "@/components/generic/Modal.vue";
import AggregateLearningLeaderboardMinimizedView from "@/components/aggregate-learning-leaderboard/AggregateLearningLeaderboardMinimizedView.vue";
import { MOCK_LEADERBOARD } from "@/components/aggregate-learning-leaderboard/mockData";
import ProgramService from "@/services/ProgramService";
import { mapState } from "vuex";

function initialState() {
  return {
    isDirty: false,
    showUnsavedModal: false,
    unsavedModalAction: null,
    saving: false,
    saveError: null,
    title: "Leaderboard",
    titleColor: "#000000",
    titleBold: false,
    titleItalic: false,
    titleUnderline: false,
    titleSize: "md",
    horizontalAlignment: "left",
    rowId: null,
  };
}

export default {
  name: "AggregateLearningLeaderboardCreator",
  components: { Button, Modal, AggregateLearningLeaderboardMinimizedView },
  props: {
    row: { type: Object, default: () => ({}) },
    column: { type: Object, default: null },
    type: { type: String, default: "aggregate-learning-leaderboard" },
    widgetTypes: { type: Array, default: () => [] },
    rowCount: { type: Number, default: 0 },
    position: { type: Number, default: 1 },
  },
  data() {
    return { ...initialState(), MOCK_LEADERBOARD };
  },
  computed: {
    ...mapState(["globalClientId"]),
    titleSizeItems() {
      return [
        { text: "Small", value: "sm" },
        { text: "Medium", value: "md" },
        { text: "Large", value: "lg" },
        { text: "Extra Large", value: "xl" },
      ];
    },
    livePreviewTitleConfig() {
      return {
        title: {
          text: this.title,
          color: this.titleColor,
          bold: this.titleBold,
          italic: this.titleItalic,
          underline: this.titleUnderline,
          size: this.titleSize,
          horizontalAlignment: this.horizontalAlignment,
        },
      };
    },
  },
  watch: {
    title() {
      this.markDirty();
    },
    titleColor() {
      this.markDirty();
    },
    titleBold() {
      this.markDirty();
    },
    titleItalic() {
      this.markDirty();
    },
    titleUnderline() {
      this.markDirty();
    },
    titleSize() {
      this.markDirty();
    },
    horizontalAlignment() {
      this.markDirty();
    },
  },
  created() {
    this._preloading = true;
    this._createdColumnId = null;
    this.preloadRow();
    this.$nextTick(() => {
      this._preloading = false;
      this.isDirty = false;
    });
  },
  methods: {
    preloadRow() {
      const column = this.column || this.row?.DashboardColumns?.[0];
      const storedConfig = column?.config;

      if (!storedConfig) {
        const widgetType = this.widgetTypes?.find((wt) => wt.type === this.type);
        const defaultTitle = widgetType?.defaultConfig?.title;
        if (defaultTitle) this.applyTitleConfig(defaultTitle);
        return;
      }

      if (storedConfig.title) this.applyTitleConfig(storedConfig.title);
    },

    applyTitleConfig(title) {
      if (!title) return;
      if (title.text !== undefined) this.title = title.text;
      if (title.color !== undefined) this.titleColor = title.color;
      if (title.bold !== undefined) this.titleBold = title.bold;
      if (title.italic !== undefined) this.titleItalic = title.italic;
      if (title.underline !== undefined) this.titleUnderline = title.underline;
      if (title.size !== undefined) this.titleSize = title.size;
      if (title.horizontalAlignment !== undefined) {
        this.horizontalAlignment = title.horizontalAlignment;
      } else if (title.align !== undefined) {
        this.horizontalAlignment = title.align;
      }
    },

    buildConfig() {
      return {
        title: {
          text: this.title,
          color: this.titleColor,
          bold: this.titleBold,
          italic: this.titleItalic,
          underline: this.titleUnderline,
          size: this.titleSize,
          horizontalAlignment: this.horizontalAlignment,
        },
      };
    },

    async onSave() {
      if (this.saving) return;
      if (!this.title.trim()) {
        this.saveError = "Title is required";
        return;
      }
      this.saving = true;
      this.saveError = null;
      try {
        const config = this.buildConfig();
        let columnId = this.column?.columnId || this._createdColumnId;
        let isNew = !this.column?.columnId && !this._createdColumnId;

        if (isNew) {
          const widgetTypeId = this.widgetTypes?.find((wt) => wt.type === this.type)?.widgetTypeId;

          if (!widgetTypeId) {
            this.saveError =
              'Widget type "aggregate-learning-leaderboard" is not registered. Backend support required.';
            return;
          }

          let rowId = this.row?.rowId || this.rowId;
          if (!rowId) {
            const rowResponse = await ProgramService.createDashboardRow({
              rowTypeId: this.row.rowTypeId,
              programId: this.row.programId,
              position: this.rowCount + 1,
              clientId: this.globalClientId,
            });
            rowId = rowResponse.data.result.rowId;
            this.rowId = rowId;
          }

          const colResponse = await ProgramService.createDashboardColumn({
            rowId,
            widgetTypeId,
            config,
            position: this.position,
            clientId: this.globalClientId,
          });
          columnId = colResponse.data.result.columnId;
          this._createdColumnId = columnId;
        } else {
          await ProgramService.updateDashboardColumn(columnId, { config });
        }

        const savedRowId = this.row?.rowId || this.rowId;
        this.$emit("saved", { columnId, config, isNew, rowId: savedRowId });
        this.$emit("refresh");
        this.$emit("close");
      } catch (e) {
        this.saveError = e.message || "Save failed";
      } finally {
        this.saving = false;
      }
    },

    handleCancel() {
      if (this.isDirty) {
        this.unsavedModalAction = "close";
        this.showUnsavedModal = true;
      } else {
        this.$emit("close");
      }
    },
    handleDiscard() {
      this.showUnsavedModal = false;
      this.$emit("close");
      this.unsavedModalAction = null;
    },
    handleStay() {
      this.showUnsavedModal = false;
      this.unsavedModalAction = null;
    },

    markDirty() {
      if (!this._preloading) this.isDirty = true;
    },
  },
};
</script>

<style scoped>
.format-btn--active {
  border: 2px solid currentColor !important;
  border-radius: 4px !important;
}

.color-swatch {
  width: 40px;
  height: 40px;
  min-width: 40px;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0;
  padding: 2px;
  appearance: none;
  -webkit-appearance: none;
}
</style>
